import { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { mutate } from "swr";

import { useFirebase } from "firebase";
import { useUser } from "context/UserContext";
import { usePOST, useGET, NetworkError } from "hooks/usePublicAPI";
import AuthLayout from "layouts/AuthLayout";
import PasswordInput from "components/Auth/PasswordInput";

const passwordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters long")
  .regex(/[0-9]/, "Password must contain at least one number");

const formSchema = z
  .object({
    currentPassword: z.string().optional(),
    password: passwordSchema,
    confirmPassword: z.string().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type FormData = z.infer<typeof formSchema>;

type AuthState = {
  recently_verified: boolean;
  last_verified_auth_at: string | null;
  verification_window_seconds: number;
};

type PasswordPayload = {
  new_password: string;
  current_password?: string;
};

type PasswordResponse = {
  success: boolean;
  message?: string;
  error?: string;
  custom_token?: string;
};

export default function SetPasswordPage() {
  const [error, setError] = useState<string | null>(null);
  const [linkSent, setLinkSent] = useState(false);
  const {
    user: firebaseUser,
    auth,
    signInWithCustomToken,
    loading: authLoading,
  } = useFirebase();
  const { user, loading: userLoading } = useUser();
  const navigate = useNavigate();

  const { data: authState, isLoading: authStateLoading } = useGET(
    firebaseUser ? "/me/auth" : null,
  ) as { data: AuthState | undefined; isLoading: boolean };

  const methods = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
  });
  const { control, handleSubmit, formState, watch } = methods;
  const isPasswordValid = passwordSchema.safeParse(watch("password")).success;

  const { trigger: triggerSetPassword, isMutating: isSettingPassword } =
    usePOST<PasswordResponse, NetworkError, PasswordPayload>("/me/password");

  const { trigger: triggerMagicLink, isMutating: isSendingLink } = usePOST<
    { message?: string; error?: string },
    NetworkError,
    { email: string; redirect_path: string }
  >("/auth/request-magic-link");

  if (!firebaseUser || !auth || !signInWithCustomToken) {
    if (!authLoading) {
      navigate("/login", { replace: true });
    }
    return null;
  }

  const hasPassword = !!user?.auth_providers.includes("password");
  const recentlyVerified = !!authState?.recently_verified;
  // A correct current password is the alternative to recent verification, but
  // only when one exists to enter.
  const needsCurrentPassword = !recentlyVerified && hasPassword;
  // With no password to fall back on and no recent verification, the user must
  // re-prove control of their email before they can set one.
  const mustVerifyFirst = !recentlyVerified && !hasPassword;
  const canUseMagicLink = !recentlyVerified && !!user?.email;

  const onSubmit = async (data: FormData) => {
    setError(null);
    if (needsCurrentPassword && !data.currentPassword) {
      setError("Please enter your current password.");
      return;
    }
    try {
      const payload: PasswordPayload = { new_password: data.password };
      if (needsCurrentPassword && data.currentPassword) {
        payload.current_password = data.currentPassword;
      }

      const response = await triggerSetPassword(payload);

      if (response?.success) {
        if (response.custom_token) {
          await signInWithCustomToken(auth, response.custom_token);
        }
        await Promise.all([mutate("/me"), mutate("/me/auth")]);
        navigate("/account", {
          replace: true,
          state: { message: "Password updated successfully." },
        });
        return;
      }
      setError(
        response?.error || response?.message || "Failed to set password.",
      );
    } catch (err) {
      const netError = err as NetworkError;
      const code = netError?.responseData?.error_code;
      if (code === "recent_verification_required") {
        setError(
          "For your security, please verify it's you before setting a new password.",
        );
      } else if (netError?.statusCode === 401) {
        setError("Incorrect current password.");
      } else {
        if (!(err instanceof NetworkError)) {
          Sentry.captureException(err);
        }
        setError(
          netError?.responseData?.error ||
            netError?.message ||
            "An unexpected error occurred. Please try again.",
        );
      }
    }
  };

  const handleSendLink = async () => {
    if (!user?.email) return;
    setError(null);
    try {
      await triggerMagicLink({
        email: user.email,
        redirect_path: "/set-password",
      });
      setLinkSent(true);
    } catch {
      setError("Couldn't send the sign-in link. Please try again.");
    }
  };

  const title = hasPassword ? "Change your password" : "Set a password";
  const description = hasPassword
    ? "Choose a new password for your account."
    : "Add a password so you can sign in faster next time.";

  return (
    <AuthLayout title={title} description={description}>
      {authStateLoading || userLoading ? (
        <div className="flex justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-cantelope border-t-transparent" />
        </div>
      ) : (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {mustVerifyFirst ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  First, confirm it's you — we'll email you a link to continue.
                </p>
                {linkSent ? (
                  <div className="rounded bg-blue-100 p-3 text-sm text-blue-700">
                    We sent a link to {user?.email}. Click it to set your
                    password.
                  </div>
                ) : canUseMagicLink ? (
                  <button
                    type="button"
                    onClick={handleSendLink}
                    disabled={isSendingLink}
                    className="flex w-full items-center justify-center rounded bg-cantelope px-6 py-3 font-bold text-soil transition-all hover:bg-soil hover:text-cantelope disabled:opacity-50"
                  >
                    {isSendingLink ? "Sending..." : "Email a link"}
                  </button>
                ) : (
                  <div className="rounded bg-red-100 p-3 text-sm text-red-700">
                    We couldn't find an email on your account. Please contact
                    support to set a password.
                  </div>
                )}
              </div>
            ) : (
              <>
                {needsCurrentPassword && (
                  <div className="space-y-2">
                    <PasswordInput
                      control={control}
                      label="Current Password"
                      name="currentPassword"
                      required
                    />
                    {canUseMagicLink &&
                      (linkSent ? (
                        <p className="text-sm text-blue-700">
                          Link sent to {user?.email}.
                        </p>
                      ) : (
                        <button
                          type="button"
                          onClick={handleSendLink}
                          disabled={isSendingLink}
                          className="px-0 text-sm text-gray-500 underline decoration-dotted hover:decoration-solid disabled:opacity-50"
                        >
                          Forgot it? Email a link instead
                        </button>
                      ))}
                  </div>
                )}
                <PasswordInput
                  control={control}
                  label="New Password"
                  name="password"
                  description="Must be at least 8 characters and contain one number."
                  required
                  autoComplete="new-password"
                />
                <PasswordInput
                  control={control}
                  label="Confirm New Password"
                  name="confirmPassword"
                  required
                  disabled={!isPasswordValid}
                  autoComplete="new-password"
                />

                <button
                  type="submit"
                  className="flex w-full items-center justify-center rounded bg-cantelope px-6 py-3 font-bold text-soil transition-all hover:bg-soil hover:text-cantelope disabled:opacity-50"
                  disabled={isSettingPassword || !formState.isValid}
                >
                  {isSettingPassword
                    ? "Saving..."
                    : hasPassword
                      ? "Update Password"
                      : "Set Password"}
                </button>
              </>
            )}

            {error && (
              <div className="rounded bg-red-100 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={() => navigate("/account")}
              className="w-full text-center text-sm text-gray-500 underline decoration-dotted hover:decoration-solid"
            >
              Cancel
            </button>
          </form>
        </FormProvider>
      )}
    </AuthLayout>
  );
}
